import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";

const MODULO = "Actas";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const RegistrarActas = () => {
  const { fichaGrupo: fichaGrupoURL, id } = useParams();
  const navigate = useNavigate();

  const [fichaGrupo, setFichaGrupo] = useState("");
  const [grupoObjectId, setGrupoObjectId] = useState("");

  const [nombreComiteReunion, setNombreComiteReunion] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [fecha, setFecha] = useState("");
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFin, setHoraFin] = useState("");
  const [lugarEnlace, setLugarEnlace] = useState("");
  const [direccionRegionalCentro, setDireccionRegionalCentro] = useState("");

  const [agenda, setAgenda] = useState([""]);
  const [objetivos, setObjetivos] = useState([""]);
  const [desarrollo, setDesarrollo] = useState([""]);
  const [conclusiones, setConclusiones] = useState([""]);

  const [compromisos, setCompromisos] = useState([
    { actividad: "", fecha: "", responsable: "" },
  ]);

  const [asistentes, setAsistentes] = useState([
    { nombre: "", dependenciaEmpresa: "", aprueba: "", observacion: "" },
  ]);

  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO ACTAS
  // ==============================
  const getModuloActas = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
          ? JSON.parse(usuarioStorage)
          : userStorage
            ? JSON.parse(userStorage)
            : usuariosStorage
              ? JSON.parse(usuariosStorage)
              : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloActas = useMemo(() => getModuloActas(), []);

  const puedeCrear =
    moduloActas?.puedeCrear === true ||
    moduloActas?.crear === true ||
    moduloActas?.create === true;

  const puedeActualizar =
    moduloActas?.puedeActualizar === true ||
    moduloActas?.actualizar === true ||
    moduloActas?.editar === true ||
    moduloActas?.update === true;

  const puedeGuardar = edit ? puedeActualizar : puedeCrear;

  const disabledAll = useMemo(
    () => loading || saving || !puedeGuardar,
    [loading, saving, puedeGuardar]
  );

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // =================== init ===================
  useEffect(() => {
    if (fichaGrupoURL) setFichaGrupo(fichaGrupoURL);
  }, [fichaGrupoURL]);

  useEffect(() => {
    setEdit(!!id);
  }, [id]);

  // =================== GETDATA ===================
  const getData = useCallback(async () => {
    try {
      if (!id) return;

      setLoading(true);

      const { data } = await axios.get(
        `/api/grupos/actas/listid/${id}`,
        authConfig()
      );

      const acta = data?.acta;

      if (!acta) throw new Error("Acta no encontrada.");

      setFichaGrupo(acta.fichaGrupo ?? "");

      setGrupoObjectId(
        typeof acta.grupoObjectId === "object"
          ? acta.grupoObjectId?._id
          : acta.grupoObjectId || ""
      );

      setNombreComiteReunion(acta.nombreComiteReunion ?? "");
      setCiudad(acta.ciudad ?? "");
      setFecha(acta.fecha ? String(acta.fecha).substring(0, 10) : "");
      setHoraInicio(acta.horaInicio ?? "");
      setHoraFin(acta.horaFin ?? "");
      setLugarEnlace(acta.lugarEnlace ?? "");
      setDireccionRegionalCentro(acta.direccionRegionalCentro ?? "");

      setAgenda(
        Array.isArray(acta.agenda) && acta.agenda.length ? acta.agenda : [""]
      );

      setObjetivos(
        Array.isArray(acta.objetivos) && acta.objetivos.length
          ? acta.objetivos
          : [""]
      );

      setDesarrollo(
        Array.isArray(acta.desarrollo) && acta.desarrollo.length
          ? acta.desarrollo
          : [""]
      );

      setConclusiones(
        Array.isArray(acta.conclusiones) && acta.conclusiones.length
          ? acta.conclusiones
          : [""]
      );

      setCompromisos(
        Array.isArray(acta.compromisos) && acta.compromisos.length
          ? acta.compromisos.map((c) => ({
              actividad: c?.actividad ?? "",
              fecha: c?.fecha ? String(c.fecha).substring(0, 10) : "",
              responsable: c?.responsable ?? "",
            }))
          : [{ actividad: "", fecha: "", responsable: "" }]
      );

      setAsistentes(
        Array.isArray(acta.asistentes) && acta.asistentes.length
          ? acta.asistentes.map((a) => ({
              nombre: a?.nombre ?? "",
              dependenciaEmpresa: a?.dependenciaEmpresa ?? "",
              aprueba: a?.aprueba ?? "",
              observacion: a?.observacion ?? "",
            }))
          : [
              {
                nombre: "",
                dependenciaEmpresa: "",
                aprueba: "",
                observacion: "",
              },
            ]
      );
    } catch (error) {
      console.error("Error al cargar el acta:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar el acta.",
      });
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  // =================== helpers ===================
  const cleanData = () => {
    const fichaKeep = fichaGrupoURL ? fichaGrupoURL : "";

    setFichaGrupo(fichaKeep);
    setGrupoObjectId("");
    setNombreComiteReunion("");
    setCiudad("");
    setFecha("");
    setHoraInicio("");
    setHoraFin("");
    setLugarEnlace("");
    setDireccionRegionalCentro("");
    setAgenda([""]);
    setObjetivos([""]);
    setDesarrollo([""]);
    setConclusiones([""]);
    setCompromisos([{ actividad: "", fecha: "", responsable: "" }]);
    setAsistentes([
      { nombre: "", dependenciaEmpresa: "", aprueba: "", observacion: "" },
    ]);
  };

  // =================== API ===================
  const saveActa = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar actas.",
        "warning"
      );
      return;
    }

    try {
      setSaving(true);

      const newActa = {
        fichaGrupo,
        grupoObjectId,
        nombreComiteReunion,
        ciudad,
        fecha,
        horaInicio,
        horaFin,
        lugarEnlace,
        direccionRegionalCentro,
        agenda,
        objetivos,
        desarrollo,
        conclusiones,
        compromisos: compromisos.map((c) => ({
          ...c,
          fecha: c.fecha ? new Date(c.fecha) : null,
        })),
        asistentes,
      };

      await axios.post("/api/grupos/actas/add", newActa, authConfig());

      Swal.fire({
        icon: "success",
        title: "¡Acta registrada con éxito!",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate(`/institucional/actas/${fichaGrupo}`);
    } catch (error) {
      console.error("Error en saveActa:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.mensaje || "No se pudo registrar el acta.",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateActa = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar actas.",
        "warning"
      );
      return;
    }

    try {
      if (!id) {
        return Swal.fire({
          icon: "error",
          title: "Error",
          text: "El ID del acta no está definido.",
        });
      }

      setSaving(true);

      const updatedActa = {
        fichaGrupo,
        nombreComiteReunion,
        ciudad,
        fecha,
        horaInicio,
        horaFin,
        lugarEnlace,
        direccionRegionalCentro,
        agenda,
        objetivos,
        desarrollo,
        conclusiones,
        compromisos: compromisos.map((c) => ({
          ...c,
          fecha: c.fecha ? new Date(c.fecha) : null,
        })),
        asistentes,
      };

      const { data } = await axios.put(
        `/api/grupos/actas/update/${id}`,
        updatedActa,
        authConfig()
      );

      Swal.fire({
        icon: "success",
        title: data?.mensaje || "Acta actualizada correctamente",
        showConfirmButton: false,
        timer: 1500,
      }).then(() => navigate(`/institucional/actas/${fichaGrupo}`));
    } catch (error) {
      console.error("Error en updateActa:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.mensaje || "Error al actualizar el acta",
      });
    } finally {
      setSaving(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    edit ? updateActa() : saveActa();
  };

  // =================== handlers dinámicos ===================
  const handleAgendaChange = (index, value) => {
    const next = [...agenda];
    next[index] = value;
    setAgenda(next);
  };

  const handleAddAgenda = () => setAgenda([...agenda, ""]);

  const handleRemoveAgenda = (index) => {
    const next = [...agenda];
    next.splice(index, 1);
    setAgenda(next.length ? next : [""]);
  };

  const handleObjetivoChange = (index, value) => {
    const next = [...objetivos];
    next[index] = value;
    setObjetivos(next);
  };

  const handleAddObjetivo = () => setObjetivos([...objetivos, ""]);

  const handleRemoveObjetivo = (index) => {
    const next = [...objetivos];
    next.splice(index, 1);
    setObjetivos(next.length ? next : [""]);
  };

  const handleDesarrolloChange = (index, value) => {
    const next = [...desarrollo];
    next[index] = value;
    setDesarrollo(next);
  };

  const handleAddDesarrollo = () => setDesarrollo([...desarrollo, ""]);

  const handleRemoveDesarrollo = (index) => {
    const next = [...desarrollo];
    next.splice(index, 1);
    setDesarrollo(next.length ? next : [""]);
  };

  const handleConclusionChange = (index, value) => {
    const next = [...conclusiones];
    next[index] = value;
    setConclusiones(next);
  };

  const handleAddConclusion = () => setConclusiones([...conclusiones, ""]);

  const handleRemoveConclusion = (index) => {
    const next = [...conclusiones];
    next.splice(index, 1);
    setConclusiones(next.length ? next : [""]);
  };

  const handleCompromisoChange = (index, campo, valor) => {
    const next = [...compromisos];
    next[index] = { ...next[index], [campo]: valor };
    setCompromisos(next);
  };

  const handleAddCompromiso = () =>
    setCompromisos([
      ...compromisos,
      { actividad: "", fecha: "", responsable: "" },
    ]);

  const handleRemoveCompromiso = (index) => {
    const next = [...compromisos];
    next.splice(index, 1);
    setCompromisos(
      next.length ? next : [{ actividad: "", fecha: "", responsable: "" }]
    );
  };

  const handleAsistenteChange = (index, campo, valor) => {
    const next = [...asistentes];
    next[index] = { ...next[index], [campo]: valor };
    setAsistentes(next);
  };

  const handleAddAsistente = () =>
    setAsistentes([
      ...asistentes,
      { nombre: "", dependenciaEmpresa: "", aprueba: "", observacion: "" },
    ]);

  const handleRemoveAsistente = (index) => {
    const next = [...asistentes];
    next.splice(index, 1);
    setAsistentes(
      next.length
        ? next
        : [
            {
              nombre: "",
              dependenciaEmpresa: "",
              aprueba: "",
              observacion: "",
            },
          ]
    );
  };

  // =================== UI helpers ===================
  const CircleDangerBtn = ({ onClick, title }) => (
    <button
      type="button"
      className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center"
      style={{ width: 34, height: 34 }}
      onClick={onClick}
      title={title}
      disabled={disabledAll}
    >
      <i className="fa-solid fa-trash" />
    </button>
  );

  const CircleAddBtn = ({ onClick, title }) => (
    <button
      type="button"
      className="btn btn-dark btn-sm d-inline-flex align-items-center justify-content-center rounded-circle"
      style={{ width: 38, height: 38 }}
      onClick={onClick}
      title={title}
      disabled={disabledAll}
    >
      <i className="fa-solid fa-plus text-white" />
    </button>
  );

  // =================== RENDER ===================
  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {edit ? "Editar acta" : "Registrar acta"}
                </h5>

                <small className="text-secondary">
                  Diligencia la información, agrega listas y guarda el acta del
                  grupo.
                </small>

                {!puedeGuardar && (
                  <div className="alert alert-warning mt-3 mb-0">
                    No tiene permisos para {edit ? "actualizar" : "registrar"}{" "}
                    actas.
                  </div>
                )}
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                  disabled={disabledAll}
                  title="Limpiar"
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {(loading || saving) && (
              <div className="text-center text-secondary py-3">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                />
                <div className="mt-2">
                  {loading ? "Cargando acta…" : "Guardando acta…"}
                </div>
              </div>
            )}

            <form onSubmit={actions}>
              <div className="row g-3">
                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold">
                    Ficha del Grupo
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Ej. 2711519"
                    value={fichaGrupo}
                    onChange={(e) => setFichaGrupo(e.target.value)}
                    disabled={disabledAll || Boolean(fichaGrupoURL) || edit}
                  />
                  {(fichaGrupoURL || edit) && (
                    <div className="form-text">Ficha bloqueada.</div>
                  )}
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Comité o Reunión
                  </label>
                  <select
                    className="form-select"
                    required
                    value={nombreComiteReunion}
                    onChange={(e) => setNombreComiteReunion(e.target.value)}
                    disabled={disabledAll}
                  >
                    <option value="">Seleccione un tipo de acta</option>
                    <option value="Inicio de Formación">
                      Inicio de Formación
                    </option>
                    <option value="Seguimiento a la Formación">
                      Seguimiento a la Formación
                    </option>
                    <option value="Evaluación y Seguimiento">
                      Evaluación y Seguimiento
                    </option>
                    <option value="Socialización Etapa Productiva">
                      Socialización Etapa Productiva
                    </option>
                    <option value="Socialización Proyecto Formativo">
                      Socialización Proyecto Formativo
                    </option>
                    <option value="Seguimiento Individual del Aprendiz">
                      Seguimiento Individual del Aprendiz
                    </option>
                    <option value="Comité Técnico del Programa">
                      Comité Técnico del Programa
                    </option>
                    <option value="Cierre de Formación">
                      Cierre de Formación
                    </option>
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Ciudad</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej. Medellín"
                    value={ciudad}
                    onChange={(e) => setCiudad(e.target.value)}
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Fecha</label>
                  <input
                    type="date"
                    className="form-control"
                    required
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold">Hora Inicio</label>
                  <input
                    type="time"
                    className="form-control"
                    value={horaInicio}
                    onChange={(e) => setHoraInicio(e.target.value)}
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-2">
                  <label className="form-label fw-semibold">Hora Fin</label>
                  <input
                    type="time"
                    className="form-control"
                    value={horaFin}
                    onChange={(e) => setHoraFin(e.target.value)}
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Lugar / Enlace Virtual
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={lugarEnlace}
                    onChange={(e) => setLugarEnlace(e.target.value)}
                    disabled={disabledAll}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Dirección Regional / Centro
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={direccionRegionalCentro}
                    onChange={(e) =>
                      setDireccionRegionalCentro(e.target.value)
                    }
                    disabled={disabledAll}
                  />
                </div>
              </div>

              <div className="mt-4">
                <ul className="nav nav-tabs" id="miTab" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link active"
                      id="agenda-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#agenda"
                      type="button"
                      role="tab"
                    >
                      Agenda
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="objetivos-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#objetivos"
                      type="button"
                      role="tab"
                    >
                      Objetivos
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="desarrollo-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#desarrollo"
                      type="button"
                      role="tab"
                    >
                      Desarrollo
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="conclusiones-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#conclusiones"
                      type="button"
                      role="tab"
                    >
                      Conclusiones
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="compromiso-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#compromiso"
                      type="button"
                      role="tab"
                    >
                      Compromisos
                    </button>
                  </li>

                  <li className="nav-item" role="presentation">
                    <button
                      className="nav-link"
                      id="asistentes-tab"
                      data-bs-toggle="tab"
                      data-bs-target="#asistentes"
                      type="button"
                      role="tab"
                    >
                      Asistentes
                    </button>
                  </li>
                </ul>

                <div
                  className="tab-content p-3 border border-top-0 bg-white"
                  id="miTabContent"
                >
                  <div
                    className="tab-pane fade show active"
                    id="agenda"
                    role="tabpanel"
                    aria-labelledby="agenda-tab"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">Agenda</h6>
                      <CircleAddBtn
                        onClick={handleAddAgenda}
                        title="Agregar punto"
                      />
                    </div>

                    {agenda.map((item, index) => (
                      <div
                        className="row g-2 align-items-center mb-2"
                        key={index}
                      >
                        <div className="col-12 col-md-11">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Punto ${index + 1}`}
                            value={item}
                            onChange={(e) =>
                              handleAgendaChange(index, e.target.value)
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                          <CircleDangerBtn
                            onClick={() => handleRemoveAgenda(index)}
                            title="Eliminar"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="tab-pane fade"
                    id="objetivos"
                    role="tabpanel"
                    aria-labelledby="objetivos-tab"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">Objetivos</h6>
                      <CircleAddBtn
                        onClick={handleAddObjetivo}
                        title="Agregar objetivo"
                      />
                    </div>

                    {objetivos.map((item, index) => (
                      <div
                        className="row g-2 align-items-center mb-2"
                        key={index}
                      >
                        <div className="col-12 col-md-11">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Objetivo ${index + 1}`}
                            value={item}
                            onChange={(e) =>
                              handleObjetivoChange(index, e.target.value)
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                          <CircleDangerBtn
                            onClick={() => handleRemoveObjetivo(index)}
                            title="Eliminar"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="tab-pane fade"
                    id="desarrollo"
                    role="tabpanel"
                    aria-labelledby="desarrollo-tab"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">Desarrollo</h6>
                      <CircleAddBtn
                        onClick={handleAddDesarrollo}
                        title="Agregar punto"
                      />
                    </div>

                    {desarrollo.map((item, index) => (
                      <div
                        className="row g-2 align-items-center mb-2"
                        key={index}
                      >
                        <div className="col-12 col-md-11">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Punto ${index + 1}`}
                            value={item}
                            onChange={(e) =>
                              handleDesarrolloChange(index, e.target.value)
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                          <CircleDangerBtn
                            onClick={() => handleRemoveDesarrollo(index)}
                            title="Eliminar"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="tab-pane fade"
                    id="conclusiones"
                    role="tabpanel"
                    aria-labelledby="conclusiones-tab"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">Conclusiones</h6>
                      <CircleAddBtn
                        onClick={handleAddConclusion}
                        title="Agregar conclusión"
                      />
                    </div>

                    {conclusiones.map((item, index) => (
                      <div
                        className="row g-2 align-items-center mb-2"
                        key={index}
                      >
                        <div className="col-12 col-md-11">
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Conclusión ${index + 1}`}
                            value={item}
                            onChange={(e) =>
                              handleConclusionChange(index, e.target.value)
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                          <CircleDangerBtn
                            onClick={() => handleRemoveConclusion(index)}
                            title="Eliminar"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="tab-pane fade"
                    id="compromiso"
                    role="tabpanel"
                    aria-labelledby="compromiso-tab"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">Compromisos</h6>
                      <CircleAddBtn
                        onClick={handleAddCompromiso}
                        title="Agregar compromiso"
                      />
                    </div>

                    {compromisos.map((item, index) => (
                      <div
                        className="row g-2 align-items-center mb-2"
                        key={index}
                      >
                        <div className="col-12 col-md-5">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Actividad"
                            value={item.actividad}
                            onChange={(e) =>
                              handleCompromisoChange(
                                index,
                                "actividad",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <input
                            type="date"
                            className="form-control"
                            value={item.fecha || ""}
                            onChange={(e) =>
                              handleCompromisoChange(
                                index,
                                "fecha",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Responsable"
                            value={item.responsable}
                            onChange={(e) =>
                              handleCompromisoChange(
                                index,
                                "responsable",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                          <CircleDangerBtn
                            onClick={() => handleRemoveCompromiso(index)}
                            title="Eliminar"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div
                    className="tab-pane fade"
                    id="asistentes"
                    role="tabpanel"
                    aria-labelledby="asistentes-tab"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">Asistentes</h6>
                      <CircleAddBtn
                        onClick={handleAddAsistente}
                        title="Agregar asistente"
                      />
                    </div>

                    {asistentes.map((asistente, index) => (
                      <div
                        className="row g-2 align-items-center mb-2"
                        key={index}
                      >
                        <div className="col-12 col-md-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Nombre"
                            value={asistente.nombre}
                            onChange={(e) =>
                              handleAsistenteChange(
                                index,
                                "nombre",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Dependencia / Empresa"
                            value={asistente.dependenciaEmpresa}
                            onChange={(e) =>
                              handleAsistenteChange(
                                index,
                                "dependenciaEmpresa",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-2">
                          <select
                            className="form-select"
                            value={asistente.aprueba}
                            onChange={(e) =>
                              handleAsistenteChange(
                                index,
                                "aprueba",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          >
                            <option value="">¿Aprueba?</option>
                            <option value="Sí">Sí</option>
                            <option value="No">No</option>
                          </select>
                        </div>

                        <div className="col-12 col-md-3">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Observación"
                            value={asistente.observacion}
                            onChange={(e) =>
                              handleAsistenteChange(
                                index,
                                "observacion",
                                e.target.value
                              )
                            }
                            disabled={disabledAll}
                          />
                        </div>

                        <div className="col-12 col-md-1 d-flex justify-content-md-center">
                          <CircleDangerBtn
                            onClick={() => handleRemoveAsistente(index)}
                            title="Eliminar"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4">
                {puedeGuardar && (
                  <button
                    type="submit"
                    className="btn btn-dark"
                    disabled={disabledAll}
                  >
                    <i
                      className={`fa-solid ${
                        saving ? "fa-spinner fa-spin" : "fa-floppy-disk"
                      } me-2`}
                    ></i>
                    {edit ? "Actualizar" : "Guardar"}
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cleanData}
                  disabled={disabledAll}
                >
                  <i className="fa-solid fa-eraser me-2"></i>
                  Limpiar
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading || saving}
                >
                  <i className="fa-solid fa-xmark me-2"></i>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarActas;